import axios from 'axios'
import path from 'path'
import axiosWrapper, { axiosRMSWrapper, getLocale } from '../helpers/api'
import { EMethods, ERequestUrl, RMSRequestUrl } from '../helpers/requests'
import { apiHeaders } from '../../utils/utils'
import { LOCAL_STORAGE_KEYS } from '../../utils/constants'

const authHeaders = () =>
  apiHeaders(localStorage.getItem(LOCAL_STORAGE_KEYS.CONNEXION_TYPE))

const rmsCertificatesCreateUrl = () => {
  const domain = process.env.RMS_API_URL || ''
  const baseUrl = domain.endsWith('/api') ? `${domain}/` : `${domain}/api/`
  return `${baseUrl}${path.join(RMSRequestUrl.fgasCapacityCertificates, '')}`
}

export interface FgasCapacityCertificate {
  id: string
  certificateNumber: string
  validityStartDate: string
  validityEndDate: string
  holderCompanyName: string
  holderSiret: string
  holderAddress: {
    address: string
    zipcode: string
    city: string
  }
  supportingDocumentUrl?: string
}

/** Raw shape returned by the API (GET list / POST create). */
interface FgasCapacityCertificateRaw {
  id: string
  certificateNumber: string
  validityStartDate: string
  validityEndDate: string
  holderCompanyName: string
  holderSiret: string
  holderAddress?: {
    address?: string
    zipcode?: string
    city?: string
    [key: string]: unknown
  }
  supportingDocument?: { name?: string; [key: string]: unknown }
  supportingDocumentUrl?: string
}

function normalizeFgasCertificate(
  raw: FgasCapacityCertificateRaw
): FgasCapacityCertificate {
  return {
    id: raw.id,
    certificateNumber: raw.certificateNumber,
    validityStartDate: raw.validityStartDate,
    validityEndDate: raw.validityEndDate,
    holderCompanyName: raw.holderCompanyName,
    holderSiret: raw.holderSiret,
    holderAddress: {
      address: raw.holderAddress?.address ?? '',
      zipcode: raw.holderAddress?.zipcode ?? '',
      city: raw.holderAddress?.city ?? '',
    },
    supportingDocumentUrl:
      raw.supportingDocumentUrl ?? raw.supportingDocument?.name,
  }
}

export type CreateFgasCapacityCertificateDto = Omit<
  FgasCapacityCertificate,
  'id' | 'supportingDocumentUrl'
> & { renter?: { email: string } }

export const getFgasCapacityCertificates = (email: string) =>
  (
    axiosRMSWrapper({
      method: EMethods.get,
      url: RMSRequestUrl.fgasCapacityCertificates,
      uid: `renters/${encodeURIComponent(email)}`,
      headers: authHeaders(),
    }) as Promise<FgasCapacityCertificateRaw[]>
  ).then((rows) => rows.map(normalizeFgasCertificate))

export const searchFgasCapacityCertificates = (search: string) =>
  (
    axiosRMSWrapper({
      method: EMethods.get,
      url: RMSRequestUrl.fgasCapacityCertificates,
      params: { search },
      headers: authHeaders(),
    }) as Promise<FgasCapacityCertificateRaw[]>
  ).then((rows) => rows.map(normalizeFgasCertificate))

export const patchOrderItemFgasCertificate = (
  cartToken: string,
  itemId: number,
  certificateId: number
) =>
  axiosWrapper({
    method: EMethods.patch,
    url: ERequestUrl.createCart,
    uid: `${cartToken}/items/${itemId}`,
    data: { fgasCapacityCertificateId: certificateId },
    headers: { 'Content-Type': 'application/merge-patch+json' },
  })

export const createFgasCapacityCertificate = (
  payload: CreateFgasCapacityCertificateDto,
  supportingDocument: File
) => {
  const form = new FormData()
  form.append('data', JSON.stringify(payload))
  form.append('locale', getLocale())
  form.append('supportingDocument', supportingDocument, supportingDocument.name)

  return axios({
    method: 'POST',
    url: rmsCertificatesCreateUrl(),
    headers: {
      ...authHeaders(),
      'x-api-key': process.env.RMS_PUBLIC_API_TOKEN || '',
    },
    data: form,
  }).then((res) => {
    const raw = res.data?.data != null ? res.data.data : res.data
    return normalizeFgasCertificate(raw as FgasCapacityCertificateRaw)
  })
}
